import { listCoachingApplicationsOptions } from "@/actions/coaching-application/handler";
import { listAllPackagesOptions } from "@/actions/coaching-package/handler";
import { listAllEssaysOptions } from "@/actions/essay/handler";
import { listAllExercisesOptions } from "@/actions/exercise/handler";
import { listAllSlidesOptions } from "@/actions/hero-slide/handler";
import { listMediaOptions } from "@/actions/media/handler";
import { listAllProductsOptions } from "@/actions/product/handler";
import { listAllProgramsOptions } from "@/actions/program/handler";
import { listAllProjectsOptions } from "@/actions/project/handler";
import { broadcastNotificationFn } from "@/actions/push/handler";
import { UrlPickerInput } from "@/components/admin/url-picker-input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { formatBytes } from "@/utils/format";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  redirect,
  useRouteContext,
} from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import {
  Activity,
  AlertTriangle,
  Bell,
  BookOpen,
  ClipboardList,
  Dumbbell,
  FileText,
  FolderOpen,
  HardDrive,
  Image,
  MessagesSquare,
  Package,
  Presentation,
  ShoppingBag,
  Target,
  Users as UsersIcon,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/admin/")({
  beforeLoad: async ({ context }) => {
    if (
      !context.user ||
      (context.user.role !== "admin" && context.user.role !== "author")
    ) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({ context }) => {
    if (context.user?.role === "author") {
      await context.queryClient.ensureQueryData(listAllEssaysOptions());
    } else {
      await context.queryClient.ensureQueryData(listMediaOptions());
      await context.queryClient.ensureQueryData(listAllPackagesOptions());
      await context.queryClient.ensureQueryData(listAllEssaysOptions());
      await context.queryClient.ensureQueryData(listAllSlidesOptions());
      await context.queryClient.ensureQueryData(listAllProductsOptions());
      await context.queryClient.ensureQueryData(listAllProjectsOptions());
      await context.queryClient.ensureQueryData(listAllExercisesOptions());
      await context.queryClient.ensureQueryData(listAllProgramsOptions());
      await context.queryClient.ensureQueryData(
        listCoachingApplicationsOptions(),
      );
    }
  },
  component: RouteComponent,
});

function BroadcastForm() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("");

  const mutation = useMutation({
    mutationFn: broadcastNotificationFn,
    onSuccess: (result) => {
      if (result.status === 200 && result.body) {
        toast.success(
          `Bildirim gönderildi: ${result.body.sent} başarılı, ${result.body.failed} başarısız`,
        );
        setTitle("");
        setBody("");
        setUrl("");
      }
    },
    onError: () => {
      toast.error("Bildirim gönderilemedi");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    mutation.mutate({
      data: {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
      },
    });
  };

  return (
    <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-primary/10 p-2">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <div>
            <CardTitle className="font-bold text-lg sm:text-xl">
              Push Bildirim Gönder
            </CardTitle>
            <p className="text-muted-foreground text-xs sm:text-sm">
              Tüm kullanıcılara bildirim gönder
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notif-title" className="text-xs">
              Başlık
            </Label>
            <Input
              id="notif-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bildirim başlığı"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="notif-body" className="text-xs">
              Mesaj
            </Label>
            <Textarea
              id="notif-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Bildirim mesajı"
              rows={3}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="notif-url" className="text-xs">
                Yönlendirme Linki (opsiyonel)
              </Label>
              <span className="text-[10px] text-muted-foreground">
                Simgeye tıklayarak sayfa seçin
              </span>
            </div>
            <UrlPickerInput
              value={url}
              onChange={setUrl}
              placeholder="Örn: /training veya özel URL"
            />
          </div>
          <Button
            type="submit"
            disabled={mutation.isPending}
            className="w-full"
          >
            {mutation.isPending
              ? "Gönderiliyor..."
              : "Tüm Kullanıcılara Gönder"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function AuthorDashboard() {
  const essaysData = useSuspenseQuery(listAllEssaysOptions());
  const essaysCount = essaysData.data.body?.essays?.length || 0;

  return (
    <div className="container mx-auto px-4 py-24 sm:px-6 lg:px-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="mb-2 font-bold text-2xl sm:text-3xl">Yazar Paneli</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Yazı ve içerik yönetimi
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Essay Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Essay'leri oluşturun ve yayınlayın
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {essaysCount} yazı
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/essay">Essay Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdminDashboard() {
  const mediaData = useSuspenseQuery(listMediaOptions());
  const packagesData = useSuspenseQuery(listAllPackagesOptions());
  const essaysData = useSuspenseQuery(listAllEssaysOptions());
  const slidesData = useSuspenseQuery(listAllSlidesOptions());
  const productsData = useSuspenseQuery(listAllProductsOptions());
  const exercisesData = useSuspenseQuery(listAllExercisesOptions());
  const projectsData = useSuspenseQuery(listAllProjectsOptions());
  const programsData = useSuspenseQuery(listAllProgramsOptions());
  const applicationsData = useSuspenseQuery(listCoachingApplicationsOptions());

  const mediaCount = mediaData.data.body?.total || 0;
  const totalSize = (mediaData.data.body as any)?.totalSize || 0;

  const MAX_STORAGE_BYTES = 5 * 1024 * 1024 * 1024; // 5GB
  const storagePercentage = Math.min(
    (totalSize / MAX_STORAGE_BYTES) * 100,
    100,
  );
  const isNearLimit = storagePercentage > 90;
  const isOverLimit = storagePercentage >= 100;

  const currentUsageFormatted = formatBytes(totalSize);
  const packagesCount = packagesData.data.body?.packages?.length || 0;

  const essaysCount = essaysData.data.body?.essays?.length || 0;
  const slidesCount = slidesData.data.body?.slides?.length || 0;
  const productsCount = productsData.data.body?.products?.length || 0;
  const exercisesCount = exercisesData.data.body?.exercises?.length || 0;
  const projectsCount = (projectsData.data as any).body?.projects?.length || 0;
  const programsCount = programsData.data.body?.programs?.length || 0;
  const applicationsCount = applicationsData.data.body.length;

  return (
    <div className="container mx-auto px-4 py-24 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="mb-2 font-bold text-2xl sm:text-3xl">Admin Paneli</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Sistem yönetimi ve içerik kontrolü
        </p>
      </div>

      {/* Storage Tracker Card */}
      <div className="mb-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card className="group relative col-span-full overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <HardDrive className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Depolama Alanı (S3)
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Aylık ücretsiz katman kullanım durumu (Maks: 5 GB)
                  </p>
                </div>
              </div>
              <span
                className={`self-start rounded-full px-3 py-1 font-semibold text-sm sm:self-auto ${isOverLimit ? "bg-destructive/10 text-destructive" : isNearLimit ? "bg-orange-500/10 text-orange-500" : "bg-primary/10 text-primary"}`}
              >
                {currentUsageFormatted} / 5.00 GB
              </span>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <Progress value={storagePercentage} className="h-3" />
            {isNearLimit && (
              <div
                className={`flex items-center gap-2 rounded-lg p-3 text-sm ${isOverLimit ? "bg-destructive/10 text-destructive" : "bg-orange-500/10 text-orange-500"}`}
              >
                <AlertTriangle className="h-5 w-5 shrink-0" />
                <p>
                  {isOverLimit
                    ? "Ücretsiz S3 kotasını aştınız! Bundan sonraki kullanımlar ücretlendirilecektir."
                    : "Dikkat: Ücretsiz depolama alanının %90'ını doldurdunuz. Sınır aşılırsa ücretlendirme başlayacaktır."}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dashboard Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Media Management Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Image className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Medya Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Storage'daki medya dosyalarını yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {mediaCount} medya
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/media">Medya Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Package Management Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Paket Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Koçluk ve danışmanlık paketlerini yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {packagesCount} paket
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/packages">Paket Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Coaching Applications Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <ClipboardList className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Koçluk Başvuruları
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Başvuruları ve reklam kaynaklarını yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {applicationsCount} başvuru
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/applications">Başvuruları Görüntüle</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Essay Management Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Essay Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Essay'leri oluşturun ve yayınlayın
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {essaysCount} yazı
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/essay">Essay Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Hero Slides Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Presentation className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Hero Slides
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Ana sayfa slider görselleri
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {slidesCount} slide
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/hero-slides">Slide Yönetimi</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Store Products Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <ShoppingBag className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Ürün Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Mağaza ürünlerini yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {productsCount} ürün
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/store">Ürün Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Central Catalog Card */}
        <Card className="group relative overflow-hidden border-2 border-primary/20 bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/40 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="font-bold text-lg sm:text-xl">
                  Merkezi Katalog
                </CardTitle>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  Dijital ürün, fiyat ve program erişimlerini yönetin
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/catalog">Katalog Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Program Management Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Dumbbell className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Program Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Antrenman programlarını yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {programsCount} program
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/programs">Program Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Exercise Management Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Activity className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Egzersiz Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Egzersiz kütüphanesini yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {exercisesCount} egzersiz
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/exercises">Egzersiz Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <FolderOpen className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg sm:text-xl">
                    Proje Yönetimi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Platformdaki projeleri yönetin
                  </p>
                </div>
              </div>
              <span className="self-start rounded-full bg-primary/10 px-3 py-1 font-semibold text-primary text-sm sm:self-auto">
                {projectsCount} proje
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/projects">Proje Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        {/* Push Notifications Card */}
        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <MessagesSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="font-bold text-lg sm:text-xl">
                  Forum Moderasyonu
                </CardTitle>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  Soruları inceleyin ve arama motorlarına açın
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/forum">Forum Moderasyonuna Git</Link>
            </Button>
          </CardContent>
        </Card>

        <Card className="group relative overflow-hidden border-2 border-border bg-card shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-primary/30 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <BookOpen className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="font-bold text-lg sm:text-xl">
                  Bilim Kütüphanesi
                </CardTitle>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  Öğrenme yollarını ve akademik kaynakları yönetin
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/library">Kütüphane Yönetimine Git</Link>
            </Button>
          </CardContent>
        </Card>

        <BroadcastForm />

        {/* User Management Card (Unified) */}
        <Card className="group relative col-span-full overflow-hidden border-2 border-primary/20 bg-secondary/5 shadow-lg transition-all duration-300 hover:border-primary/40 hover:shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/20 p-2">
                  <UsersIcon className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="font-bold text-lg text-primary sm:text-xl">
                    Kullanıcı ve Koçluk Takibi
                  </CardTitle>
                  <p className="text-muted-foreground text-xs sm:text-sm">
                    Tüm üyelerin ilerlemelerini, aktiflik durumlarını ve koçluk
                    danışanlarının günlük verilerini tek bir yerden izleyin
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 sm:flex-row">
            <Button asChild className="flex-1 shadow-lg shadow-primary/20">
              <Link to="/admin/users">Tüm Kullanıcıları Görüntüle</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              className="flex-1 border-primary/20 hover:bg-primary/5"
            >
              <Link to="/admin/users" search={{ filter: "clients" }}>
                <Target className="mr-2 h-4 w-4" />
                Sadece Danışanları Takip Et
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function RouteComponent() {
  const context = useRouteContext({ from: "/_app/admin/" });
  if (context.user?.role === "author") {
    return <AuthorDashboard />;
  }
  return <AdminDashboard />;
}
